import {
  Badge,
  Button,
  Card,
  CellText,
  DataTable,
  EmptyState,
  FormField,
  Icon,
  ListToolbar,
  MoreMenu,
  PageHeader,
  Select,
  Text,
  type DataTableColumn,
} from '@ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { errorMessage } from '../../../lib/errors';
import { formatDate } from '../../../lib/format';
import { useFacetTexts } from '../../../lib/facets';
import { useColumnSort, useSortMenu } from '../../../lib/useColumnSort';
import { useResetUrlState, useUrlState } from '../../../lib/useUrlState';
import { useSession } from '../../identity/session';
import { useExportItems } from '../../reports/components/ExportButton';
import { useBatches, useVarieties, type BatchListItem } from '../api';
import { useRowMenu } from '../../../app/rowActions';
import { useBatchActions } from '../components/BatchActions';
import { BatchFormDialog } from '../components/BatchFormDialog';
import { formatKg, formatPercent } from '../format';
import styles from './HarvestPages.module.css';

const FILTER_KEYS = ['q', 'jahr', 'sorte'] as const;
const ALL_YEARS = 'alle';

/** Batches with search and filters by year and variety (HRV-005). */
export function BatchesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { can } = useSession();
  const thisYear = String(new Date().getFullYear());
  const years = Array.from({ length: 6 }, (_, i) => String(Number(thisYear) - i));
  const [year, setYear] = useUrlState('jahr', thisYear, [...years, ALL_YEARS]);
  const [variety, setVariety] = useUrlState('sorte', '');
  const [q, setQ] = useUrlState('q', '');
  const resetFilters = useResetUrlState(FILTER_KEYS);
  const facetTexts = useFacetTexts(resetFilters);
  const columnSort = useColumnSort();
  const [creating, setCreating] = useState(false);
  const rowMenu = useRowMenu();
  const batchActions = useBatchActions();
  const batches = useBatches({
    year: year === ALL_YEARS ? undefined : Number(year),
    variety_id: variety || undefined,
  });
  const varieties = useVarieties(true);
  const exportItems = useExportItems('batches');
  const needle = q.trim().toLocaleLowerCase('de');
  const rows = (batches.data ?? []).filter(
    (b) =>
      !needle ||
      [b.lot, b.variety.name, b.location?.name ?? ''].some((s) =>
        s.toLocaleLowerCase('de').includes(needle),
      ),
  );
  const filtered = variety !== '' || needle !== '' || year !== thisYear;
  const varietyName = varieties.data?.find((v) => v.id === variety)?.name;
  const total = rows.reduce((s, b) => s + b.weight_grams, 0);
  const bulk = rows.reduce((s, b) => s + b.bulk_grams, 0);
  const jars = rows.reduce((s, b) => s + b.jars, 0);
  const write = can('harvest.write');
  const create = () => setCreating(true);

  const chips = [
    ...(year !== thisYear
      ? [
          {
            key: 'year',
            label:
              year === ALL_YEARS
                ? t('harvest.filters.allYears')
                : t('harvest.filters.yearChip', { year }),
            onRemove: () => setYear(thisYear),
          },
        ]
      : []),
    ...(variety
      ? [
          {
            key: 'variety',
            label: t('harvest.filters.varietyChip', { name: varietyName ?? '…' }),
            onRemove: () => setVariety(''),
          },
        ]
      : []),
  ];

  const columns: DataTableColumn<BatchListItem>[] = [
    {
      key: 'lot',
      header: t('harvest.list.lot'),
      primary: true,
      cell: (b) => b.lot,
      sortValue: (b) => b.lot,
    },
    {
      key: 'variety',
      header: t('harvest.list.variety'),
      cell: (b) => b.variety.name,
      sortValue: (b) => b.variety.name,
    },
    {
      key: 'date',
      header: t('harvest.list.date'),
      cell: (b) => formatDate(b.harvested_on),
      sortValue: (b) => b.harvested_on,
    },
    {
      key: 'location',
      header: t('harvest.list.location'),
      mobile: 'hidden',
      cell: (b) => b.location?.name ?? (b.colonies > 0 ? t('harvest.batch.mixedShort') : '–'),
      sortValue: (b) => b.location?.name ?? (b.colonies > 0 ? t('harvest.batch.mixedShort') : null),
    },
    {
      key: 'weight',
      header: t('harvest.list.weight'),
      align: 'end',
      cell: (b) => formatKg(b.weight_grams),
      sortValue: (b) => b.weight_grams,
    },
    {
      key: 'water',
      header: t('harvest.list.water'),
      align: 'end',
      cell: (b) =>
        b.water_content == null ? (
          '–'
        ) : b.water_warning ? (
          <Badge tone="warning" size="sm" title={t('harvest.water.warningTitle')}>
            {formatPercent(b.water_content)}
          </Badge>
        ) : (
          formatPercent(b.water_content)
        ),
      sortValue: (b) => b.water_content,
    },
    {
      key: 'stock',
      header: t('harvest.list.stock'),
      align: 'end',
      cell: (b) => (
        <CellText
          main={formatKg(b.bulk_grams)}
          sub={b.jars > 0 ? t('harvest.articles.count', { count: b.jars }) : null}
        />
      ),
      sortValue: (b) => b.bulk_grams,
    },
  ];

  const sortMenu = useSortMenu(columns, rows, columnSort);

  return (
    <div className={styles.page}>
      <PageHeader
        title={t('harvest.title')}
        actions={
          <>
            {write && (
              <Button iconStart={<Icon name="plus" size="sm" />} onClick={create}>
                {t('harvest.batch.new')}
              </Button>
            )}
            {exportItems.length > 0 && (
              <MoreMenu label={t('common.moreActions')} items={exportItems} />
            )}
          </>
        }
      />
      <ListToolbar
        sort={sortMenu}
        facets={{
          ...facetTexts,
          items: [
            {
              type: 'select',
              key: 'year',
              label: t('harvest.filters.year'),
              value: year,
              neutral: thisYear,
              options: [
                ...years.map((y) => ({ value: y, label: y })),
                { value: ALL_YEARS, label: t('harvest.filters.allYears') },
              ],
              onChange: setYear,
            },
            {
              type: 'select',
              key: 'variety',
              label: t('harvest.filters.variety'),
              value: variety,
              options: (varieties.data ?? []).map((v) => ({ value: v.id, label: v.name })),
              onChange: setVariety,
            },
          ],
        }}
        search={{
          value: q,
          onChange: setQ,
          label: t('common.search'),
          clearLabel: t('common.clear'),
        }}
        filters={{
          label: t('common.filter'),
          activeCount: chips.length,
          content: (
            <>
              <FormField label={t('harvest.filters.year')}>
                {(p) => (
                  <Select
                    {...p}
                    value={year}
                    onChange={(e) => setYear(e.target.value)}
                    options={[
                      ...years.map((y) => ({ value: y, label: y })),
                      { value: ALL_YEARS, label: t('harvest.filters.allYears') },
                    ]}
                  />
                )}
              </FormField>
              <FormField label={t('harvest.filters.variety')}>
                {(p) => (
                  <Select
                    {...p}
                    value={variety}
                    onChange={(e) => setVariety(e.target.value)}
                    options={[
                      { value: '', label: t('harvest.filters.allVarieties') },
                      ...(varieties.data ?? []).map((v) => ({ value: v.id, label: v.name })),
                    ]}
                  />
                )}
              </FormField>
            </>
          ),
        }}
        chips={{
          chips,
          removeLabel: (label) => t('common.removeFilter', { label }),
          resetLabel: t('common.resetAll'),
          onReset: resetFilters,
        }}
      />
      <div className={styles.table}>
        <Card padding="none">
          {batches.error ? (
            <Text tone="danger" role="alert" style={{ padding: 'var(--space-4)' }}>
              {errorMessage(batches.error)}
            </Text>
          ) : (
            <DataTable
              caption={t('harvest.list.caption')}
              columns={columns}
              rows={rows}
              rowKey={(b) => b.id}
              rowHref={(b) => `/ernte/${b.id}`}
              rowActions={rowMenu.table<BatchListItem>(
                (b) => b.lot,
                (b) => batchActions.items(b.id, { bulkGrams: b.bulk_grams }),
              )}
              {...columnSort}
              loading={batches.isPending}
              empty={
                filtered ? (
                  <EmptyState
                    title={t('common.noResults')}
                    actions={
                      <Button variant="secondary" onClick={resetFilters}>
                        {t('common.resetFilters')}
                      </Button>
                    }
                  />
                ) : (
                  <EmptyState
                    icon="honeycomb"
                    title={t('harvest.list.emptyYear', { year })}
                    actions={write && <Button onClick={create}>{t('harvest.batch.new')}</Button>}
                  />
                )
              }
            />
          )}
        </Card>
        {rows.length > 0 && (
          <Text size="sm" tone="muted" className={styles.summary}>
            {t('harvest.list.summary', {
              weight: formatKg(total, 1),
              bulk: formatKg(bulk, 1),
              jars: t('harvest.articles.count', { count: jars }),
            })}
          </Text>
        )}
      </div>
      {creating && (
        <BatchFormDialog
          open
          onClose={() => setCreating(false)}
          onSaved={(b) => void navigate(`/ernte/${b.id}`)}
        />
      )}
      {batchActions.dialogs}
    </div>
  );
}
